import { useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/components/auth/AuthContext'
import { useNeedsYou } from '@/pages/authenticated/home/useNeedsYou'
import { ClayCountBadge } from '@/components/ClayPunctuation'
import { pageShortTitles } from '@/lib/pageTitles'
import { PageHeaderIcon } from './PageHeaderIcon'
import { isPortalNavActive } from './portalNav'
import styles from './BottomTabBar.module.css'

/**
 * The new-IA mobile navigation: five always-visible thumb targets (≤768px
 * only; it isn't rendered on desktop, see AuthenticatedLayout). The mobile
 * app-bar menu still opens the full drawer, which is where Money and the
 * rest of the rail live. Badges: Attention = everything waiting on you (the
 * Needs your attention count the rail shows), Messages = threads waiting on a
 * reply. Both derive from the same `useNeedsYou()` feed Home renders, so the
 * numbers always agree with it.
 *
 * Tab marks are the same clay-relief family as PageHeaderBar; "Attention" is
 * the short name for Needs your attention where eight characters is the room
 * (`pageShortTitles`, which the phone header shares).
 */
export function BottomTabBar() {
  const { pathname } = useLocation()
  const { accountId } = useAuth()
  const feed = useNeedsYou(accountId ?? '')

  // Text threads only: Messages lists SMS conversations, so a caller waiting
  // on a callback (a VOICE thread) counts under Attention, not here.
  const messagesCount = useMemo(
    () =>
      feed.items.filter((i) => i.kind === 'conversation' && i.conversation?.type === 'SMS')
        .length,
    [feed.items],
  )

  const tabs: { href: string; label: string; name?: string; badge: number }[] = [
    { href: '/home', label: 'Home', badge: 0 },
    { href: '/queue', label: pageShortTitles['/queue'], name: 'Needs your attention', badge: feed.count },
    { href: '/messages', label: 'Messages', badge: messagesCount },
    { href: '/aria', label: 'Brain', badge: 0 },
    { href: '/customers-v2', label: 'Customers', badge: 0 },
  ]

  return (
    <nav className={styles.bar} aria-label="Primary">
      {tabs.map(({ href, label, name, badge }) => {
        const active = isPortalNavActive(pathname, href)
        return (
          <Link
            key={href}
            to={href}
            className={`${styles.tab} ${active ? styles.tabActive : ''}`}
            aria-current={active ? 'page' : undefined}
            aria-label={name ? (badge > 0 ? `${name}, ${badge} waiting` : name) : undefined}
          >
            <span className={styles.iconWrap}>
              <PageHeaderIcon
                pathname={href}
                state={active ? 'active' : 'idle'}
                className={styles.tabIcon}
              />
              {badge > 0 && (
                <ClayCountBadge
                  count={badge}
                  label={`${badge} waiting`}
                  size="sm"
                  className={styles.badge}
                />
              )}
            </span>
            {label}
          </Link>
        )
      })}
    </nav>
  )
}
